'use client';

import {
	BN,
	BigNum,
	getTriggerPrice,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import { useMemo } from 'react';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';
import useVelocityClient from './useVelocityClient';
import useOraclePrice from './useOraclePrice';

/**
 * Returns the SDK reference/trigger price for a perp market, or
 * ZERO_PRICE_BIGNUM when the velocity client, oracle price, or market
 * account are unavailable (or the computation throws).
 */
const useReferencePrice = (marketId: MarketId | undefined): BigNum => {
	const velocityClient = useVelocityClient();
	const oraclePrice = useOraclePrice(marketId);
	const marketIndex = marketId?.marketIndex;

	return useMemo(() => {
		if (!velocityClient || !oraclePrice || marketIndex === undefined) {
			return ZERO_PRICE_BIGNUM;
		}

		try {
			const marketAccount =
				velocityClient.getPerpMarketAccountOrThrow(marketIndex);

			const now = new BN(Math.floor(Date.now() / 1000)); // Current timestamp in seconds (integer)
			const referencePriceBN = getTriggerPrice(
				marketAccount,
				oraclePrice.val,
				now,
				true
			);

			return new BigNum(referencePriceBN, PRICE_PRECISION_EXP);
		} catch (error) {
			console.warn('Error calculating trigger price:', error);

			return ZERO_PRICE_BIGNUM;
		}
	}, [velocityClient, oraclePrice, marketIndex]);
};

export default useReferencePrice;
